import type { VehicleFilters } from "../types";
import type { ListVehiclesQuery } from "../client-messages/vehicles";

/**
 * Centralised (de)serialisation for vehicle search filters.
 *
 * There are three representations of the same filter state, and historically
 * they drifted apart (e.g. `attributes` was JSON in one path and a
 * comma-separated key list in another, which threw on `JSON.parse`). To keep
 * them in lock-step every conversion lives here and is unit-tested:
 *
 *  - API query params  — what `useVehicles` sends to the client API.
 *  - URL search params — what the web search screen reads/writes for shareable
 *    links.
 *  - Navigation params — what "Find same plate" / "Find similar" pass to the
 *    sightings screen.
 *
 * `attributes` is always encoded as JSON so that non-boolean values (e.g.
 * `{ doors: 4 }`) survive the round-trip. `loadStatuses` is a multi-select, so
 * it is encoded as a comma-separated `loadStatus` value on the wire and the
 * backend forwards it verbatim to the upstream VT API. The VT API parses
 * `loadStatus` as a CSV list (`lower(load_status) = ANY(...)`), so a single
 * value remains backward-compatible — see vehicle-tracker!62.
 */

function sevenDaysAgo(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString();
}

function now(): string {
  return new Date().toISOString();
}

/**
 * Produces a fresh default filter set with dates computed at call time.
 * This avoids the stale-date problem where a long-lived session would freeze
 * the "through now" window at module-load time.
 */
export function getDefaultFilters(): VehicleFilters {
  return {
    selectedCameras: {},
    types: [],
    startDate: sevenDaysAgo(),
    endDate: now(),
    plateText: "",
    description: "",
    make: "",
    model: "",
    color: "",
    loadStatuses: [],
    attributes: {},
    limit: 50,
  };
}

function encodeAttributes(attributes: Record<string, unknown>): string | null {
  const entries = Object.entries(attributes).filter(([, v]) => v !== undefined && v !== false && v !== null);
  if (entries.length === 0) return null;
  return JSON.stringify(Object.fromEntries(entries));
}

/** Parse a JSON-encoded attributes value, tolerating malformed input. */
export function decodeAttributes(raw: string | undefined): Record<string, unknown> {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Fall through — ignore malformed attribute payloads.
  }
  return {};
}

function selectedCameraEntries(selectedCameras: Record<string, string[]>): string[] {
  return Object.entries(selectedCameras).flatMap(([assetId, cams]) => (cams ?? []).map((cam) => `${assetId}:${cam}`));
}

function decodeCameras(raw: string | undefined): Record<string, string[]> {
  const record: Record<string, string[]> = {};
  if (!raw) return record;
  for (const entry of raw.split(",").filter(Boolean)) {
    const [assetId, camId] = entry.split(":");
    if (assetId && camId) {
      const arr = record[assetId] ?? [];
      arr.push(camId);
      record[assetId] = arr;
    }
  }
  return record;
}

/**
 * Build the query params sent to the vehicle-tracker client API. `selectedSiteId`
 * is merged in as `siteIds` so the backend can narrow authorised assets by site.
 */
export function filtersToApiQuery(
  filters: VehicleFilters,
  selectedSiteId: string | null,
  cursor?: string,
  sort?: "asc" | "desc",
): ListVehiclesQuery {
  const cameraAssetIds = Object.keys(filters.selectedCameras).filter(
    (id) => (filters.selectedCameras[id] ?? []).length > 0,
  );
  const pairedCameraIds = cameraAssetIds.flatMap((assetId) =>
    (filters.selectedCameras[assetId] ?? []).map((cam) => `${assetId}:${cam}`),
  );
  const encodedAttributes = encodeAttributes(filters.attributes);

  return {
    startDate: filters.startDate,
    endDate: filters.endDate,
    ...(selectedSiteId && { siteIds: selectedSiteId }),
    ...(cameraAssetIds.length > 0 && { assetIds: cameraAssetIds.join(",") }),
    ...(pairedCameraIds.length > 0 && { cameraIds: pairedCameraIds.join(",") }),
    ...(filters.types.length > 0 && { types: filters.types.join(",") }),
    ...(filters.plateText.trim().length >= 4 && {
      plateText: filters.plateText.trim(),
      plateSearchMode: "fuzzy" as const,
      plateMaxDistance: "2",
    }),
    ...(filters.description.trim() && { description: filters.description.trim() }),
    ...(filters.make.trim() && { make: filters.make.trim() }),
    ...(filters.model.trim() && { model: filters.model.trim() }),
    ...(filters.color.trim() && { color: filters.color.trim() }),
    ...(filters.loadStatuses.length > 0 && { loadStatus: filters.loadStatuses.join(",") }),
    ...(encodedAttributes && { attributes: encodedAttributes }),
    ...(sort && { sort }),
    limit: String(filters.limit),
    ...(cursor && { cursor }),
  };
}

/** Encode only the non-default filter values as shareable URL search params. */
export function filtersToUrlParams(filters: VehicleFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.plateText.trim().length >= 4) params["plateText"] = filters.plateText.trim();
  if (filters.make.trim()) params["make"] = filters.make.trim();
  if (filters.model.trim()) params["model"] = filters.model.trim();
  if (filters.color.trim()) params["color"] = filters.color.trim();
  if (filters.description.trim()) params["description"] = filters.description.trim();
  if (filters.types.length > 0) params["types"] = filters.types.join(",");
  if (filters.loadStatuses.length > 0) params["loadStatuses"] = filters.loadStatuses.join(",");
  const encodedAttributes = encodeAttributes(filters.attributes);
  if (encodedAttributes) params["attributes"] = encodedAttributes;
  if (filters.startDate) params["startDate"] = filters.startDate;
  if (filters.endDate) params["endDate"] = filters.endDate;
  const cameraEntries = selectedCameraEntries(filters.selectedCameras);
  if (cameraEntries.length > 0) params["cameras"] = cameraEntries.join(",");
  return params;
}

/**
 * Encode ALL filter keys as route params (empty string for cleared values).
 * Unlike `filtersToUrlParams`, this emits every key so that `router.setParams`
 * (which shallow-merges) fully overwrites stale values from previous commits.
 */
export function filtersToRouteParams(filters: VehicleFilters): Record<string, string> {
  const encodedAttributes = encodeAttributes(filters.attributes);
  const cameraEntries = selectedCameraEntries(filters.selectedCameras);
  return {
    plateText: filters.plateText.trim(),
    make: filters.make.trim(),
    model: filters.model.trim(),
    color: filters.color.trim(),
    description: filters.description.trim(),
    types: filters.types.join(","),
    loadStatuses: filters.loadStatuses.join(","),
    attributes: encodedAttributes ?? "",
    startDate: filters.startDate,
    endDate: filters.endDate,
    cameras: cameraEntries.join(","),
  };
}

/** Decode URL/navigation params into a partial filter set. */
export function paramsToFilters(params: Record<string, string | string[] | undefined>): Partial<VehicleFilters> {
  const partial: Partial<VehicleFilters> = {};
  const str = (v: string | string[] | undefined): string => (Array.isArray(v) ? (v[0] ?? "") : (v ?? ""));

  if (str(params["plateText"])) partial.plateText = str(params["plateText"]);
  if (str(params["make"])) partial.make = str(params["make"]);
  if (str(params["model"])) partial.model = str(params["model"]);
  if (str(params["color"])) partial.color = str(params["color"]);
  if (str(params["description"])) partial.description = str(params["description"]);
  if (str(params["types"])) partial.types = str(params["types"]).split(",").filter(Boolean);
  if (str(params["loadStatuses"])) partial.loadStatuses = str(params["loadStatuses"]).split(",").filter(Boolean);
  if (str(params["startDate"])) partial.startDate = str(params["startDate"]);
  if (str(params["endDate"])) partial.endDate = str(params["endDate"]);
  const attributes = decodeAttributes(str(params["attributes"]) || undefined);
  if (Object.keys(attributes).length > 0) partial.attributes = attributes;
  const cameras = decodeCameras(str(params["cameras"]) || undefined);
  if (Object.keys(cameras).length > 0) partial.selectedCameras = cameras;
  return partial;
}

/**
 * Stable signature of a filter set, used to detect changes for debouncing and
 * refetching. Keys are sorted so the signature does not depend on object
 * insertion order (`JSON.stringify` is order-sensitive).
 */
export function filterSignature(filters: VehicleFilters): string {
  return stableStringify(filters);
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}
